import React from 'react';
import { CheckboxInput } from 'Views/Components/New/CheckboxInput';
import { TextInput } from 'Views/Components/New/TextInput';
import { PhoneInput } from 'Views/Components/New/PhoneInput';
import { TextArea } from 'Views/Components/New/TextArea';
import { NumberInput } from 'Views/Components/New/NumberInput';
import { SelectInput } from 'Views/Components/New/SelectInput';
import { getMessageByKey } from 'Stores/LanguageStore';
import { CachedDynamicField } from 'Stores/DynamicBookingFieldsStore';

interface Props {
	field: CachedDynamicField;
	value: string;
	onChange: (next: string) => void;
	error?: string;
	readOnly?: boolean;
	/**
	 * Overrides whether the input shows as required. Defaults to the field's
	 * `required` flag (used in the booking wizard); check-in passes the field's
	 * `requiredInCheckIn` flag instead.
	 */
	requiredOverride?: boolean;
}

const parseSelectOptions = (raw: string | null | undefined): string[] => {
	if (!raw) return [];
	try {
		const parsed = JSON.parse(raw);
		return Array.isArray(parsed) ? parsed.map(String) : [];
	} catch {
		return [];
	}
};

/**
 * Renders a single dynamic-field input based on its FieldType. Resolves the
 * displayed label/placeholder via i18n (LabelKey) with the entity label as
 * fallback. Visibility filtering by customer type happens upstream in
 * DynamicFieldsSection.
 */
export default function DynamicFieldRenderer({ field, value, onChange, error, readOnly, requiredOverride }: Props) {
	const required = requiredOverride ?? field.required;
	const label = field.labelKey ? (getMessageByKey(field.labelKey, field.label) ?? field.label) : field.label;
	const placeholder = field.placeholderKey
		? (getMessageByKey(field.placeholderKey, field.placeholder ?? '') ?? field.placeholder ?? '')
		: (field.placeholder ?? '');
	// Resolve tooltip copy via i18n key first, falling back to the entity content.
	let tooltip: string | undefined;
	if (field.tooltipEnabled) {
		if (field.tooltipContentKey) {
			tooltip =
				getMessageByKey(field.tooltipContentKey, field.tooltipContent ?? '') ?? field.tooltipContent ?? '';
		} else {
			tooltip = field.tooltipContent ?? '';
		}
	}

	const commonProps = {
		label,
		placeholder,
		tooltip,
		required,
		error,
		readOnly,
		'data-test-id': `dynamic-field-${field.id}`,
	};

	switch (field.fieldType) {
		case 'TEXTAREA':
			return (
				<TextArea
					{...commonProps}
					value={value}
					onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => onChange(e.target.value)}
				/>
			);
		case 'NUMBER':
			return (
				<NumberInput
					{...commonProps}
					value={value === '' ? undefined : Number(value)}
					onChange={next => onChange(next == null ? '' : String(next))}
				/>
			);
		case 'TOGGLE':
			return (
				<CheckboxInput
					label={label}
					checked={value === 'true'}
					onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange(e.target.checked ? 'true' : 'false')}
					disabled={readOnly}
					description={placeholder}
				/>
			);
		case 'SELECT': {
			const options = parseSelectOptions(field.selectOptions).map(opt => ({ value: opt, label: opt }));
			const selectedOption = options.find(o => o.value === value) ?? null;
			return (
				<SelectInput
					label={label}
					required={required}
					value={selectedOption}
					onChange={(opt: any) => onChange(String(opt?.value ?? ''))}
					options={options}
					isDisabled={readOnly}
					error={error}
				/>
			);
		}
		case 'EMAIL':
			return (
				<TextInput
					{...commonProps}
					type="email"
					inputMode="email"
					value={value}
					onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
				/>
			);
		case 'PHONE':
			return <PhoneInput {...commonProps} value={value} onChange={(next: string) => onChange(next)} />;
		case 'TEXT':
		default:
			return (
				<TextInput
					{...commonProps}
					value={value}
					onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
				/>
			);
	}
}
